/**
 * Send Custom Notification Tab
 *
 * Admin interface for sending custom notifications to users
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Paper,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Stack,
} from '@mui/material';
import { Visibility, Notifications, Warning, People, Image as ImageIcon, Close, History, ExpandMore, ExpandLess, OpenInNew } from '@mui/icons-material';
import { httpsCallable } from 'firebase/functions';
import {
  UserProfile,
  firebaseManager,
  ref,
  uploadBytes,
  getDownloadURL,
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  where,
} from '@watchlist/shared';
import { font } from '../../utils/fontHelper';
import { RecipientPickerBottomSheet } from './RecipientPickerBottomSheet';
import { AdminPublishPreviewShell } from './AdminPublishPreviewShell';
import { NotificationSendPreviewPanel } from './NotificationSendPreviewPanel';

interface SendNotificationTabProps {
  isDark: boolean;
}

interface AnnouncementHistory {
  id: string;
  title: string;
  message: string;
  recipientType: 'all' | 'specific' | 'role';
  recipientCount: number;
  recipientIds?: string[] | null;
  role?: string | null;
  navigationTarget?: string | null;
  imageUrl?: string | null;
  threadId?: string | null;
  sentAt: string;
  sentBy: string;
  errors?: string[] | null;
}

const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

const SendNotificationTab: React.FC<SendNotificationTabProps> = ({ isDark }) => {
  const [recipientType, setRecipientType] = useState<'all' | 'specific' | 'role'>('all');
  const [selectedRecipients, setSelectedRecipients] = useState<UserProfile[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [role, setRole] = useState<string>('regular');
  const [title, setTitle] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [navigationTarget, setNavigationTarget] = useState<string>('');
  /** Firestore `crewPosts` document ID — sent as `/?postId=...` */
  const [crewPostDocId, setCrewPostDocId] = useState<string>('');
  const [threadId, setThreadId] = useState<string>('');
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [attachmentPreviewUrl, setAttachmentPreviewUrl] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string; recipientCount?: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // History state
  const [history, setHistory] = useState<AnnouncementHistory[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [expandedHistoryId, setExpandedHistoryId] = useState<string | null>(null);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [recipientPreviewLoading, setRecipientPreviewLoading] = useState(false);
  const [recipientPreviewLines, setRecipientPreviewLines] = useState<string[]>([]);

  // Fetch announcement history
  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const db = await firebaseManager.getFirestore();
      // Simple query without orderBy first to avoid index issues
      // Firestore auto-indexes single-field queries but compound queries need manual indexes
      const q = query(
        collection(db, 'adminAnnouncements'),
        orderBy('sentAt', 'desc'),
        limit(20)
      );
      const snapshot = await getDocs(q);
      console.log(`📢 Fetched ${snapshot.docs.length} announcements from adminAnnouncements`);
      const announcements: AnnouncementHistory[] = snapshot.docs.map((doc) => doc.data() as AnnouncementHistory);
      setHistory(announcements);
    } catch (err: unknown) {
      console.error('Failed to fetch announcement history:', err);
      // If index error, try without orderBy
      if (err && typeof err === 'object' && 'message' in err && String(err.message).includes('index')) {
        console.log('📢 Index error detected, trying simple query...');
        try {
          const db = await firebaseManager.getFirestore();
          const simpleQ = query(collection(db, 'adminAnnouncements'), limit(20));
          const snapshot = await getDocs(simpleQ);
          const announcements: AnnouncementHistory[] = snapshot.docs
            .map((doc) => doc.data() as AnnouncementHistory)
            .sort((a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime());
          setHistory(announcements);
        } catch (fallbackErr) {
          console.error('Fallback query also failed:', fallbackErr);
        }
      }
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const clearAttachment = useCallback(() => {
    setAttachmentFile(null);
    if (attachmentPreviewUrl) {
      URL.revokeObjectURL(attachmentPreviewUrl);
      setAttachmentPreviewUrl(null);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [attachmentPreviewUrl]);

  const onPickImage = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (!file.type.startsWith('image/')) {
        setError('Please choose an image file');
        return;
      }
      if (file.size > MAX_IMAGE_BYTES) {
        setError('Image must be 5MB or smaller');
        return;
      }
      setError(null);
      if (attachmentPreviewUrl) {
        URL.revokeObjectURL(attachmentPreviewUrl);
      }
      setAttachmentFile(file);
      setAttachmentPreviewUrl(URL.createObjectURL(file));
    },
    [attachmentPreviewUrl]
  );

  const uploadAnnouncementImage = useCallback(async (): Promise<string | undefined> => {
    if (!attachmentFile) return undefined;
    const auth = await firebaseManager.getAuth();
    const uid = auth.currentUser?.uid;
    if (!uid) {
      throw new Error('You must be signed in to upload an image');
    }
    const storage = await firebaseManager.getStorage();
    const ext = attachmentFile.name.split('.').pop() || 'jpg';
    const safeExt = ext.replace(/[^a-z0-9]/gi, '').slice(0, 8) || 'jpg';
    const objectPath = `announcement_images/${uid}/${crypto.randomUUID()}.${safeExt}`;
    const storageRef = ref(storage, objectPath);
    await uploadBytes(storageRef, attachmentFile, {
      contentType: attachmentFile.type || 'image/jpeg',
    });
    return getDownloadURL(storageRef);
  }, [attachmentFile]);

  const loadRecipientPreview = useCallback(async () => {
    setRecipientPreviewLoading(true);
    try {
      const db = await firebaseManager.getFirestore();
      const col = collection(db, 'userProfiles');

      if (recipientType === 'specific') {
        const lines: string[] = [`${selectedRecipients.length} selected recipient(s)`];
        selectedRecipients.slice(0, 14).forEach((u) => {
          lines.push(` · ${u.displayName || u.username || u.email || u.uid}`);
        });
        if (selectedRecipients.length > 14) {
          lines.push(` · …and ${selectedRecipients.length - 14} more`);
        }
        setRecipientPreviewLines(lines);
        return;
      }

      if (recipientType === 'all') {
        const snap = await getDocs(col);
        setRecipientPreviewLines([`All registered users (${snap.size})`]);
        return;
      }

      if (role === 'regular') {
        const snap = await getDocs(col);
        const n = snap.docs.filter((d) => {
          const r = d.data().role as string | undefined;
          return r === undefined || r === null || r === 'regular';
        }).length;
        setRecipientPreviewLines([`Users with role “regular” (includes unset) (${n})`]);
        return;
      }

      const q = query(col, where('role', '==', role));
      const snap = await getDocs(q);
      setRecipientPreviewLines([`Users with role “${role}” (${snap.size})`]);
    } catch (e) {
      console.error('Recipient preview count failed:', e);
      setRecipientPreviewLines(['Could not load exact count; the server will resolve recipients when you publish.']);
    } finally {
      setRecipientPreviewLoading(false);
    }
  }, [recipientType, role, selectedRecipients]);

  useEffect(() => {
    if (previewOpen) {
      loadRecipientPreview();
    }
  }, [previewOpen, loadRecipientPreview]);

  const openPreview = () => {
    if (!title.trim() || !message.trim()) {
      setError('Title and message are required');
      return;
    }
    if (recipientType === 'specific' && selectedRecipients.length === 0) {
      setError('Select at least one recipient');
      return;
    }
    setError(null);
    setPreviewOpen(true);
  };

  const navigationSummaryLabel = (() => {
    const crewId = crewPostDocId.trim();
    if (crewId) return `Tap opens Crew → post ${crewId}`;
    const nav = navigationTarget.trim();
    if (nav) return `Tap opens ${nav.startsWith('/') ? nav : `/${nav}`}`;
    return null;
  })();

  const executePublish = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const functions = await firebaseManager.getFunctions();
      const sendNotification = httpsCallable(functions, 'sendCustomNotification');

      let imageUrl: string | undefined;
      if (attachmentFile) {
        imageUrl = await uploadAnnouncementImage();
      }

      const crewId = crewPostDocId.trim();
      const resolvedNavTarget = crewId
        ? `/?postId=${encodeURIComponent(crewId)}`
        : navigationTarget.trim() || '';

      const payload: Record<string, unknown> = {
        recipientType,
        title: title.trim(),
        message: message.trim(),
        ...(resolvedNavTarget && { navigationType: 'screen', navigationTarget: resolvedNavTarget }),
        ...(threadId && { threadId }),
        ...(imageUrl && { imageUrl }),
        priority: 'high',
      };

      if (recipientType === 'specific') {
        payload.userIds = selectedRecipients.map((u) => u.uid);
      }

      if (recipientType === 'role') {
        payload.role = role;
      }

      const response = await sendNotification(payload);
      const data = response.data as { success: boolean; message: string; recipientCount: number };

      setResult(data);

      if (data.success) {
        setPreviewOpen(false);
        setTitle('');
        setMessage('');
        setNavigationTarget('');
        setCrewPostDocId('');
        setThreadId('');
        setSelectedRecipients([]);
        clearAttachment();
        fetchHistory();
      }
    } catch (err: unknown) {
      console.error('Error sending notification:', err);
      const msg =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: unknown }).message)
          : 'Failed to send notification';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Notifications sx={{ fontSize: 28, color: '#7D7AFF' }} />
        <Typography
          variant="h5"
          sx={{
            fontFamily: font(),
            fontWeight: 600,
            color: isDark ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.87)',
          }}
        >
          Send Custom Notification
        </Typography>
      </Box>

      <Alert severity="warning" icon={<Warning />} sx={{ mb: 3 }}>
        <Typography sx={{ fontFamily: font(), fontSize: 14 }}>
          Use this feature carefully. Preview how push and in-app notifications will look, then publish to send to all selected recipients.
        </Typography>
      </Alert>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
          borderRadius: 3,
          border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
        }}
      >
        <FormControl component="fieldset" sx={{ mb: 3 }}>
          <FormLabel
            sx={{
              fontFamily: font(),
              fontWeight: 600,
              color: isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.87)',
              mb: 1,
            }}
          >
            Recipients
          </FormLabel>
          <RadioGroup value={recipientType} onChange={(e) => setRecipientType(e.target.value as 'all' | 'specific' | 'role')}>
            <FormControlLabel
              value="all"
              control={<Radio />}
              label={
                <Box>
                  <Typography sx={{ fontFamily: font(), fontWeight: 600 }}>All Users</Typography>
                  <Typography sx={{ fontFamily: font(), fontSize: 12, color: 'text.secondary' }}>
                    Send to every user in the app
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="role"
              control={<Radio />}
              label={
                <Box>
                  <Typography sx={{ fontFamily: font(), fontWeight: 600 }}>By Role</Typography>
                  <Typography sx={{ fontFamily: font(), fontSize: 12, color: 'text.secondary' }}>
                    Send to users with specific role
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="specific"
              control={<Radio />}
              label={
                <Box>
                  <Typography sx={{ fontFamily: font(), fontWeight: 600 }}>Specific Users</Typography>
                  <Typography sx={{ fontFamily: font(), fontSize: 12, color: 'text.secondary' }}>
                    Choose users from your directory (multi-select)
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>
        </FormControl>

        {recipientType === 'role' && (
          <TextField
            select
            fullWidth
            label="Role"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            SelectProps={{
              native: true,
            }}
            sx={{ mb: 3 }}
          >
            <option value="regular">Regular Users</option>
            <option value="admin">Admins</option>
            <option value="moderator">Moderators</option>
            <option value="pending">Pending Users</option>
          </TextField>
        )}

        {recipientType === 'specific' && (
          <Box sx={{ mb: 3 }}>
            <Button
              variant="outlined"
              startIcon={<People />}
              onClick={() => setPickerOpen(true)}
              fullWidth
              sx={{ fontFamily: font(), textTransform: 'none', py: 1.25, mb: 1.5 }}
            >
              {selectedRecipients.length === 0
                ? 'Choose recipients…'
                : `${selectedRecipients.length} recipient${selectedRecipients.length === 1 ? '' : 's'} selected`}
            </Button>
            {selectedRecipients.length > 0 && (
              <Stack direction="row" flexWrap="wrap" gap={0.75}>
                {selectedRecipients.slice(0, 12).map((u) => (
                  <Chip
                    key={u.uid}
                    label={u.displayName || u.username || u.email || u.uid}
                    size="small"
                    onDelete={() => setSelectedRecipients((prev) => prev.filter((x) => x.uid !== u.uid))}
                    sx={{ fontFamily: font() }}
                  />
                ))}
                {selectedRecipients.length > 12 && (
                  <Chip label={`+${selectedRecipients.length - 12} more`} size="small" variant="outlined" />
                )}
              </Stack>
            )}
          </Box>
        )}

        <Divider sx={{ my: 3 }} />

        <TextField
          fullWidth
          label="Title"
          placeholder="e.g., New Feature Released!"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          sx={{ mb: 2 }}
          inputProps={{ maxLength: 100 }}
          helperText={
            title.length > 30
              ? `${title.length}/100 - iOS shows ~30 chars on lock screen`
              : `${title.length}/100 characters (30 recommended for iOS)`
          }
          error={title.length > 50}
        />

        <TextField
          fullWidth
          multiline
          rows={4}
          label="Message"
          placeholder="e.g., Check out our new movie recommendation feature in the app!"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          sx={{ mb: 2 }}
          inputProps={{ maxLength: 500 }}
          helperText={`${message.length}/500 characters`}
        />

        <Typography
          sx={{
            fontFamily: font(),
            fontWeight: 600,
            fontSize: 14,
            mb: 1,
            color: 'text.secondary',
          }}
        >
          Notification image (optional)
        </Typography>
        <Box sx={{ mb: 3 }}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={onPickImage}
          />
          <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
            <Button
              variant="outlined"
              startIcon={<ImageIcon />}
              onClick={() => fileInputRef.current?.click()}
              sx={{ fontFamily: font(), textTransform: 'none' }}
            >
              {attachmentFile ? 'Change image' : 'Upload image'}
            </Button>
            {attachmentFile && (
              <Button
                variant="text"
                color="inherit"
                startIcon={<Close />}
                onClick={clearAttachment}
                sx={{ fontFamily: font(), textTransform: 'none' }}
              >
                Remove
              </Button>
            )}
          </Stack>
          {attachmentPreviewUrl && (
            <Box
              component="img"
              src={attachmentPreviewUrl}
              alt="Attachment preview"
              sx={{
                mt: 1.5,
                maxWidth: '100%',
                maxHeight: 200,
                borderRadius: 2,
                objectFit: 'contain',
                border: 1,
                borderColor: 'divider',
              }}
            />
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontFamily: font() }}>
            Shown in in-app notifications. Push images work on Android only (iOS requires Service Extension).
          </Typography>
        </Box>

        <Typography
          sx={{
            fontFamily: font(),
            fontWeight: 600,
            fontSize: 14,
            mb: 2,
            color: 'text.secondary',
          }}
        >
          Optional Settings
        </Typography>

        <TextField
          fullWidth
          label="Official Crew post — Firestore document ID (optional)"
          placeholder="Paste crewPosts doc ID from Moderator console"
          value={crewPostDocId}
          onChange={(e) => setCrewPostDocId(e.target.value)}
          sx={{ mb: 2 }}
          helperText="When set, opens Crew and scrolls to this post. Takes priority over the generic navigation path below."
        />

        <TextField
          fullWidth
          label="Navigation Target (optional)"
          placeholder="e.g., /movie/tmdb_123456, /watchlist/abc123, /?postId=YOUR_DOC_ID"
          value={navigationTarget}
          onChange={(e) => setNavigationTarget(e.target.value)}
          sx={{ mb: 2 }}
          disabled={!!crewPostDocId.trim()}
          helperText={
            crewPostDocId.trim()
              ? 'Clear the Crew post ID above to use this field instead.'
              : 'In-app path: /movie/…, /watchlist/…, /profile/…, /?postId=…, /trending, /settings, etc.'
          }
        />

        <TextField
          fullWidth
          label="Thread ID (optional)"
          placeholder="e.g., announcement_2026"
          value={threadId}
          onChange={(e) => setThreadId(e.target.value)}
          sx={{ mb: 3 }}
          helperText="Groups related notifications together on iOS/Android"
        />

        <Button
          fullWidth
          variant="contained"
          size="large"
          startIcon={<Visibility />}
          onClick={openPreview}
          disabled={loading || !title.trim() || !message.trim()}
          sx={{
            py: 1.5,
            backgroundColor: '#7D7AFF',
            color: 'white',
            fontFamily: font(),
            fontWeight: 600,
            fontSize: 16,
            textTransform: 'none',
            '&:hover': {
              backgroundColor: '#6B68E5',
            },
            '&:disabled': {
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
              color: isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
            },
          }}
        >
          Preview
        </Button>

        {result && (
          <Alert severity="success" sx={{ mt: 3 }}>
            <Typography sx={{ fontFamily: font(), fontWeight: 600 }}>{result.message}</Typography>
            {result.recipientCount !== undefined && (
              <Chip
                label={`${result.recipientCount} recipients`}
                size="small"
                color="success"
                sx={{ mt: 1, fontFamily: font() }}
              />
            )}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 3 }}>
            <Typography sx={{ fontFamily: font() }}>{error}</Typography>
          </Alert>
        )}
      </Paper>

      <AdminPublishPreviewShell
        open={previewOpen}
        onClose={() => !loading && setPreviewOpen(false)}
        title="Preview notification"
        onPublish={executePublish}
        publishing={loading}
        publishLabel="Publish"
      >
        <NotificationSendPreviewPanel
          title={title}
          message={message}
          imagePreviewUrl={attachmentPreviewUrl}
          navigationSummary={navigationSummaryLabel}
          recipientLines={recipientPreviewLines}
          recipientLoading={recipientPreviewLoading}
          isDark={isDark}
        />
      </AdminPublishPreviewShell>

      <RecipientPickerBottomSheet
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        isDark={isDark}
        initialSelected={selectedRecipients}
        onConfirm={setSelectedRecipients}
      />

      {/* Sent Notifications History */}
      <Box sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <History sx={{ fontSize: 22, color: '#7D7AFF' }} />
          <Typography
            sx={{
              fontFamily: font(),
              fontWeight: 600,
              fontSize: 16,
              color: isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.87)',
            }}
          >
            Previously Sent Notifications
          </Typography>
        </Box>

        {historyLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} sx={{ color: '#7D7AFF' }} />
          </Box>
        ) : history.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: 3,
              textAlign: 'center',
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.01)',
              border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`,
              borderRadius: 2,
            }}
          >
            <Typography sx={{ fontFamily: font(), color: 'text.secondary', fontSize: 14 }}>
              No notifications sent yet
            </Typography>
          </Paper>
        ) : (
          <Stack spacing={1.5}>
            {history.map((item) => {
              const isExpanded = expandedHistoryId === item.id;
              const sentDate = new Date(item.sentAt);
              const formattedDate = sentDate.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
              });

              return (
                <Paper
                  key={item.id}
                  elevation={0}
                  sx={{
                    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.01)',
                    border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
                    borderRadius: 2,
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    onClick={() => setExpandedHistoryId(isExpanded ? null : item.id)}
                    sx={{
                      p: 2,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 2,
                      '&:hover': {
                        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
                      },
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography
                          sx={{
                            fontFamily: font(),
                            fontWeight: 600,
                            fontSize: 14,
                            color: isDark ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.87)',
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Chip
                          label={
                            item.recipientType === 'all'
                              ? 'All Users'
                              : item.recipientType === 'role'
                                ? `Role: ${item.role}`
                                : `${item.recipientCount} users`
                          }
                          size="small"
                          sx={{
                            fontFamily: font(),
                            fontSize: 11,
                            height: 20,
                            backgroundColor: isDark ? 'rgba(125, 122, 255, 0.2)' : 'rgba(125, 122, 255, 0.1)',
                            color: '#7D7AFF',
                          }}
                        />
                      </Box>
                      <Typography
                        sx={{
                          fontFamily: font(),
                          fontSize: 13,
                          color: 'text.secondary',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: isExpanded ? 'normal' : 'nowrap',
                        }}
                      >
                        {item.message}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: font(),
                          fontSize: 12,
                          color: 'text.disabled',
                          mt: 0.5,
                        }}
                      >
                        {formattedDate} · {item.recipientCount} recipient{item.recipientCount !== 1 ? 's' : ''}
                      </Typography>
                    </Box>
                    {isExpanded ? (
                      <ExpandLess sx={{ color: 'text.secondary', fontSize: 20 }} />
                    ) : (
                      <ExpandMore sx={{ color: 'text.secondary', fontSize: 20 }} />
                    )}
                  </Box>

                  {isExpanded && (
                    <Box
                      sx={{
                        px: 2,
                        pb: 2,
                        pt: 0,
                        borderTop: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`,
                      }}
                    >
                      <Stack spacing={1} sx={{ mt: 1.5 }}>
                        {item.navigationTarget && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography sx={{ fontFamily: font(), fontSize: 12, color: 'text.secondary' }}>
                              Navigation:
                            </Typography>
                            <Chip
                              label={item.navigationTarget}
                              size="small"
                              icon={<OpenInNew sx={{ fontSize: 12 }} />}
                              sx={{ fontFamily: font(), fontSize: 11, height: 22 }}
                            />
                          </Box>
                        )}
                        {item.imageUrl && (
                          <Box>
                            <Typography sx={{ fontFamily: font(), fontSize: 12, color: 'text.secondary', mb: 0.5 }}>
                              Attached Image:
                            </Typography>
                            <Box
                              component="img"
                              src={item.imageUrl}
                              alt="Notification image"
                              sx={{
                                maxWidth: 200,
                                maxHeight: 120,
                                borderRadius: 1,
                                objectFit: 'cover',
                              }}
                            />
                          </Box>
                        )}
                        {item.threadId && (
                          <Typography sx={{ fontFamily: font(), fontSize: 12, color: 'text.secondary' }}>
                            Thread ID: {item.threadId}
                          </Typography>
                        )}
                        {item.errors && item.errors.length > 0 && (
                          <Alert severity="warning" sx={{ py: 0.5 }}>
                            <Typography sx={{ fontFamily: font(), fontSize: 12 }}>
                              {item.errors.length} error{item.errors.length !== 1 ? 's' : ''} occurred
                            </Typography>
                          </Alert>
                        )}
                      </Stack>
                    </Box>
                  )}
                </Paper>
              );
            })}
          </Stack>
        )}
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Example Use Cases */}
      <Box>
        <Typography
          sx={{
            fontFamily: font(),
            fontWeight: 600,
            fontSize: 16,
            mb: 2,
            color: isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.87)',
          }}
        >
          Example Use Cases
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {[
            { title: 'Feature Release', message: 'New feature: Smart movie recommendations based on your taste!' },
            { title: 'Maintenance Notice', message: 'Scheduled maintenance on Sunday 2AM-4AM. App may be unavailable.' },
            {
              title: 'Community Update',
              message: "We've reached 10,000 users! Thank you for being part of our community.",
            },
          ].map((example, index) => (
            <Paper
              key={index}
              elevation={0}
              sx={{
                p: 2,
                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.01)',
                border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`,
                borderRadius: 2,
              }}
            >
              <Typography sx={{ fontFamily: font(), fontWeight: 600, fontSize: 14, mb: 0.5 }}>
                {example.title}
              </Typography>
              <Typography sx={{ fontFamily: font(), fontSize: 13, color: 'text.secondary' }}>
                {example.message}
              </Typography>
            </Paper>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default SendNotificationTab;
